<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row g-2 align-items-center">
            <div class="col-md-3 col-lg-2">
              <DatePicker v-model="requestDTO.fromDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                <template v-slot="{ inputValue, togglePopover, inputEvents }">
                  <input
                    class="form-control form-control-sm"
                    :placeholder="helper.trans('labels.from_date')"
                    :value="inputValue"
                    @focus="togglePopover"
                    @change="inputEvents.change"
                  />
                </template>
              </DatePicker>
            </div>
            <div class="col-md-3 col-lg-2">
              <DatePicker v-model="requestDTO.toDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                <template v-slot="{ inputValue, togglePopover, inputEvents }">
                  <input
                    class="form-control form-control-sm"
                    :placeholder="helper.trans('labels.to_date')"
                    :value="inputValue"
                    @focus="togglePopover"
                    @change="inputEvents.change"
                  />
                </template>
              </DatePicker>
            </div>
            <div class="col-auto ms-md-auto">
              <button type="button" @click.prevent="applyFilters" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">{{ helper.trans('button.apply') }}</button>
              <button type="button" @click.prevent="clearFilters" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
            </div>
          </div>

          <div class="row g-2 mt-2">
            <div class="col-md-3 col-lg-2">
              <Multiselect
                class="multiselect-sm"
                v-model="cascadesFilter"
                :options="cascadesOptions"
                :custom-label="(opt) => opt.name"
                track-by="id"
                :multiple="true"
                select-label="Select"
                :placeholder="helper.trans('labels.cascades')"
                :searchable="true"
                :internal-search="true"
                :clear-on-select="false"
                :close-on-select="false"
                :hide-selected="true"
              />
            </div>
            <div class="col-md-3 col-lg-2">
              <Multiselect
                class="multiselect-sm"
                v-model="providersFilter"
                :options="providersOptions"
                :custom-label="(opt) => `${opt.name} (${opt.sysname})`"
                track-by="id"
                :multiple="true"
                select-label="Select"
                :placeholder="helper.trans('labels.providers')"
                :searchable="true"
                :internal-search="true"
                :clear-on-select="false"
                :close-on-select="false"
                :hide-selected="true"
              />
            </div>
            <div class="col-md-3 col-lg-2">
              <Multiselect
                class="multiselect-sm"
                v-model="ratingTypesFilter"
                :options="ratingTypeOptions"
                :custom-label="ratingTypeLabel"
                :multiple="true"
                select-label="Select"
                :placeholder="helper.trans('statistic.cascadeRating.rating_type')"
                :searchable="false"
                :clear-on-select="false"
                :close-on-select="false"
                :hide-selected="true"
              />
            </div>
            <div class="col-md-3 col-lg-2">
              <TrafficGroupMultiselect :multiple="true" small v-model="trafficGroupsFilter" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!response.items.length" class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0 text-center text-muted py-3">
          {{ helper.trans('statistic.cascadeRating.no_data') }}
        </div>
      </div>
    </div>

    <div v-else class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div v-for="snapshot in response.items" :key="snapshot.id" class="card card-body mb-3">
          <div class="mb-2 d-flex flex-wrap align-items-center gap-2">
            <span class="text-muted">{{ helper.trans('statistic.cascadeRating.cascade') }}:</span>
            <strong>{{ snapshot.cascade.name }}</strong>
            <span class="text-muted ms-2">{{ helper.trans('statistic.cascadeRating.bucket_date') }}: {{ formatBucketDate(snapshot.bucketDate) }}</span>
            <span class="badge bg-secondary ms-2">{{ ratingTypeLabel(snapshot.ratingType) }}</span>
          </div>
          <div class="overflow-auto">
            <table class="table table-bordered align-middle text-left text-nowrap position-relative mb-0">
              <thead>
                <tr class="bg-light">
                  <th class="text-capitalize-first text-end">
                    <a href="#" @click.prevent="toggleSnapshotOrderBy(snapshot.id, 'id')">
                      {{ helper.trans('statistic.cascadeRating.provider_id') }}
                      <i class="fa-solid" :class="orderByClassForSnapshot(snapshot.id, 'id')"></i>
                    </a>
                  </th>
                  <th class="text-capitalize-first">{{ helper.trans('statistic.cascadeRating.provider') }}</th>
                  <th class="text-capitalize-first text-end">
                    <a href="#" @click.prevent="toggleSnapshotOrderBy(snapshot.id, 'score')">
                      {{ helper.trans('statistic.cascadeRating.score') }}
                      <i class="fa-solid" :class="orderByClassForSnapshot(snapshot.id, 'score')"></i>
                    </a>
                  </th>
                  <th class="text-capitalize-first text-end">
                    <a href="#" @click.prevent="toggleSnapshotOrderBy(snapshot.id, 'conversion')">
                      {{ helper.trans('statistic.cascadeRating.conversion') }}
                      <i class="fa-solid" :class="orderByClassForSnapshot(snapshot.id, 'conversion')"></i>
                    </a>
                  </th>
                  <th class="text-capitalize-first text-end">
                    <a href="#" @click.prevent="toggleSnapshotOrderBy(snapshot.id, 'avgTime')">
                      {{ helper.trans('statistic.cascadeRating.avg_time') }}
                      <i class="fa-solid" :class="orderByClassForSnapshot(snapshot.id, 'avgTime')"></i>
                    </a>
                  </th>
                  <th class="text-capitalize-first text-end">
                    <a href="#" @click.prevent="toggleSnapshotOrderBy(snapshot.id, 'margin')">
                      {{ helper.trans('statistic.cascadeRating.margin') }}
                      <i class="fa-solid" :class="orderByClassForSnapshot(snapshot.id, 'margin')"></i>
                    </a>
                  </th>
                  <th class="text-capitalize-first text-center">
                    <a href="#" @click.prevent="toggleSnapshotOrderBy(snapshot.id, 'position')">
                      {{ helper.trans('statistic.cascadeRating.position') }}
                      <i class="fa-solid" :class="orderByClassForSnapshot(snapshot.id, 'position')"></i>
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="provider in sortedProviders(snapshot)" :key="provider.id">
                  <td class="text-end">{{ provider.id }}</td>
                  <td>
                    <span v-b-tooltip.hover :title="provider.sysname">{{ provider.name || provider.sysname }}</span>
                  </td>
                  <td class="text-end">{{ formatMetric(provider.score) }}</td>
                  <td class="text-end">{{ formatConversion(provider.conversion) }}</td>
                  <td class="text-end">{{ formatAvgTime(provider.avgTime) }}</td>
                  <td class="text-end">{{ formatMetric(provider.margin) }}</td>
                  <td class="text-center">{{ provider.position }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <b-pagination
          v-if="response.totalCount > response.items.length"
          v-model="page"
          :total-rows="response.totalCount"
          :per-page="response.limit"
          :limit="20"
          @update:modelValue="load()"
          prev-text="Back"
          next-text="Next"
          align="start"
          first-number
          hide-goto-end-buttons
          hide-ellipsis
        ></b-pagination>
      </div>
    </div>
  </LoaderWrapper>
</template>
<script lang="ts">
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {defineComponent, onMounted, reactive, ref, toRefs} from "vue";
import {dateHelper} from "@/service/DateHelper";
import {api} from "@/service/APIClient";
import {helper} from "@/service/CommonHelper";
import {useNotification} from "@kyvg/vue3-notification";
import {DatePicker} from "v-calendar";
import Multiselect from "vue-multiselect";
import {BPagination} from "bootstrap-vue-3";
import moment from "moment";
import CascadeRatingSnapshotDTO, {CascadeRatingSnapshotProviderDTO} from "@/models/CascadeRatingSnapshotDTO";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import OrderByDTO from "@/models/OrderByDTO";
import TrafficGroupMultiselect from "@/components/TrafficGroupMultiselect.vue";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import CascadeDTO from "@/models/CascadeDTO";
import ProviderDTO from "@/models/ProviderDTO";

const notification = useNotification();

const RATING_TYPE_OPTIONS = ['conversion', 'mix_conversion'] as const;

type SortableField = 'id' | 'score' | 'conversion' | 'avgTime' | 'margin' | 'position';

function defaultRequestDTO() {
  return {
    fromDate: moment().subtract(24, 'hours').format('YYYY-MM-DD HH:mm:ss'),
    toDate: moment().format('YYYY-MM-DD HH:mm:ss'),
  };
}

function emptyResponse(): PaginatedResponseDTO<CascadeRatingSnapshotDTO> {
  return {
    limit: 20,
    offset: 0,
    totalCount: 0,
    items: [],
  };
}

function emptyOrderBy(): OrderByDTO {
  return {field: null, direction: null};
}

function sortValue(provider: CascadeRatingSnapshotProviderDTO, field: SortableField): number {
  switch (field) {
    case 'id':
      return provider.id;
    case 'position':
      return provider.position;
    case 'score':
    case 'conversion':
    case 'avgTime': {
      const raw = provider[field];
      if (raw == null || raw === '') {
        return Number.NEGATIVE_INFINITY;
      }
      const num = Number(raw);
      return Number.isNaN(num) ? Number.NEGATIVE_INFINITY : num;
    }
    case 'margin': {
      if (provider.margin == null || provider.margin === '') {
        return Number.NEGATIVE_INFINITY;
      }
      const num = Number(provider.margin);
      return Number.isNaN(num) ? Number.NEGATIVE_INFINITY : num;
    }
    default:
      return 0;
  }
}

export default defineComponent({
  components: {
    LoaderWrapper,
    DatePicker,
    Multiselect,
    TrafficGroupMultiselect,
    BPagination,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);

    const state = reactive({
      page: 1,
      response: emptyResponse(),
      cascadesOptions: [] as CascadeDTO[],
      providersOptions: [] as ProviderDTO[],
      cascadesFilter: [] as CascadeDTO[],
      providersFilter: [] as ProviderDTO[],
      ratingTypesFilter: [] as string[],
      trafficGroupsFilter: [] as TrafficGroupDTO[],
      requestDTO: defaultRequestDTO(),
      orderByBySnapshotId: {} as Record<number, OrderByDTO>,
    });

    const ratingTypeOptions = [...RATING_TYPE_OPTIONS];

    const toggleSnapshotOrderBy = (snapshotId: number, field: SortableField) => {
      const current = state.orderByBySnapshotId[snapshotId] ?? emptyOrderBy();
      const next = {...current};
      helper.toggleOrderBy(next, field);
      state.orderByBySnapshotId = {
        ...state.orderByBySnapshotId,
        [snapshotId]: next,
      };
    };

    const orderByClassForSnapshot = (snapshotId: number, field: SortableField) =>
      helper.orderByClass(state.orderByBySnapshotId[snapshotId] ?? emptyOrderBy(), field);

    const sortedProviders = (snapshot: CascadeRatingSnapshotDTO) => {
      const orderBy = state.orderByBySnapshotId[snapshot.id];
      const field = orderBy?.field as SortableField | null | undefined;
      const direction = orderBy?.direction;
      if (!field || !direction) {
        return snapshot.providers;
      }

      return [...snapshot.providers].sort((a, b) => {
        const aValue = sortValue(a, field);
        const bValue = sortValue(b, field);
        if (aValue === bValue) {
          return 0;
        }
        if (direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
      });
    };

    const ratingTypeLabel = (ratingType: string) => {
      if (ratingType === 'conversion') {
        return helper.trans('statistic.cascadeRating.rating_type_conversion');
      }
      if (ratingType === 'mix_conversion') {
        return helper.trans('statistic.cascadeRating.rating_type_mix');
      }
      return ratingType;
    };

    const formatBucketDate = (value: string) => dateHelper.format(value, 'DD.MM.YYYY HH:mm:ss');

    const formatMetric = (value: string | number | null | undefined) => {
      if (value == null || value === '') {
        return '—';
      }
      return value;
    };

    const formatConversion = (value: string | number | null | undefined) => {
      if (value == null || value === '') {
        return '—';
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return value;
      }
      return `${(num * 100).toFixed(2)}%`;
    };

    const formatAvgTime = (value: string | number | null | undefined) => {
      if (value == null || value === '') {
        return '—';
      }
      const num = Number(value);
      if (Number.isNaN(num)) {
        return value;
      }
      return `${num} ${helper.trans('statistic.cascadeRating.seconds_suffix')}`;
    };

    const buildParams = () => {
      const params: Record<string, unknown> = {
        fromDate: dateHelper.toUTC(state.requestDTO.fromDate),
        toDate: dateHelper.toUTC(state.requestDTO.toDate),
        limit: state.response.limit,
        offset: state.response.limit * (state.page - 1),
      };
      const cascadeIds = state.cascadesFilter.map((c) => c.id);
      const providerIds = state.providersFilter.map((p) => p.id);
      const trafficGroupsIds = state.trafficGroupsFilter.map((g) => g.id);
      if (cascadeIds.length) params.cascadeIds = cascadeIds;
      if (providerIds.length) params.providerIds = providerIds;
      if (state.ratingTypesFilter.length) params.ratingTypes = state.ratingTypesFilter;
      if (trafficGroupsIds.length) params.trafficGroupsIds = trafficGroupsIds;
      return params;
    };

    const load = async () => {
      loader.value?.show();
      try {
        state.response = await api.statisticCascadeRatingSnapshots(buildParams());
      } catch (error) {
        const err = error as { response?: { data?: { message?: string }[] }; message?: string };
        notification.notify({
          type: 'error',
          title: helper.trans('labels.error'),
          text: err?.response?.data?.[0]?.message ?? (error instanceof Error ? error.message : String(error)),
        });
      } finally {
        loader.value?.hide();
      }
    };

    const applyFilters = () => {
      state.page = 1;
      load();
    };

    const clearFilters = () => {
      state.cascadesFilter = [];
      state.providersFilter = [];
      state.ratingTypesFilter = [];
      state.trafficGroupsFilter = [];
      state.requestDTO = defaultRequestDTO();
      state.orderByBySnapshotId = {};
      state.page = 1;
      load();
    };

    const loadFilterOptions = async () => {
      const [cascades, providers] = await Promise.all([
        api.call.cascades.all(),
        api.call.providers.all(),
      ]);
      state.cascadesOptions = cascades;
      state.providersOptions = providers;
    };

    onMounted(async () => {
      try {
        await loadFilterOptions();
      } catch (error) {
        // options load failure should not block the table
      }
      await load();
    });

    return {
      ...toRefs(state),
      loader,
      helper,
      ratingTypeOptions,
      sortedProviders,
      toggleSnapshotOrderBy,
      orderByClassForSnapshot,
      load,
      applyFilters,
      clearFilters,
      ratingTypeLabel,
      formatBucketDate,
      formatMetric,
      formatConversion,
      formatAvgTime,
    };
  },
});
</script>
